import { withErrorBoundary } from '@sentry/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery, useSubscription } from 'urql';
import { assets } from '../../../assets';
import { BlurredContainerM2030 } from '../../../components/containers/containers';
import DefaultScreen from '../../../components/containers/default-screen';
import StyledReactSelect from '../../../components/inputs/styled-react-select';
import InfiniteList from '../../../components/lists/infinite-list';
import ListControls from '../../../components/lists/list-controls';
import ListHeader from '../../../components/lists/list-header';
import { Fallback } from '../../../components/utils/error-boundary';
import { formatDateTime } from '../../../helpers/date-formatters';
import {
  getStatusOptions,
  getSecurityTransfersOrderingOptions,
} from '../../../helpers/type-options';
import { GraphqlOperations } from '../../../logic/graphql/operations';
import {
  Account,
  OrderingDirection,
  OrderingType,
  Query,
  SecurityListType,
  SecurityTransferListItem,
  SecurityType,
  Status,
  Subscription,
} from '../../../logic/graphql/types/types.d';
import { Paths } from '../../../navigation/paths';
import ListItem from './components/security-transfer-list-item';
import { FormattedMessage, useIntl } from 'react-intl';

const SecurityTransfers = (): JSX.Element => {
  // MARK: - Resources
  const history = useHistory();
  const intl = useIntl();

  const statusOptions = useMemo(() => {
    return getStatusOptions(intl, 'nopartialorexpired');
  }, [intl]);

  const securityTransfersOrderingOptions = useMemo(() => {
    return getSecurityTransfersOrderingOptions(intl);
  }, [intl]);

  // MARK: - State
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [selectedSecurities, setSelectedInstruments] = useState<string[]>([]);
  const [selectedOrderingType, setSelectedOrderingType] = useState<OrderingType>(
    OrderingType.TransferDate,
  );
  const [selectedOrderingDirection, setSelectedOrderingDirection] = useState<OrderingDirection>(
    OrderingDirection.Desc,
  );
  const [selectedStatus, setSelectedStatus] = useState<Status | 'all'>(Status.Active);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [items, setItems] = useState<SecurityTransferListItem[]>([]);

  // MARK: - Queries
  const [sumPortfolioLastUpdateQueryResult, reexecuteSumPortfolioLastUpdateQuery] = useQuery<Query>(
    {
      query: GraphqlOperations.queries.sum_portfolioLastUpdate.queryString,
      requestPolicy: 'cache-and-network',
    },
  );

  const [itemsResultQuery, reexecuteItemsResultQuery] = useQuery<Query>({
    query: GraphqlOperations.queries.get_security_transfers.queryString,
    variables: GraphqlOperations.queries.get_security_transfers.createVars(
      currentPage,
      10,
      selectedOrderingType,
      selectedOrderingDirection,
      selectedStatus,
      selectedSecurities,
      selectedAccount,
    ),
    requestPolicy: 'network-only',
    pause: selectedAccount === '',
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  const fetchNextPage = (startIndex: number, stopIndex: number): any => {
    if (startIndex / 10 >= currentPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const userAccountsQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.getUserAccounts.queryString,
  })[0];

  const securitiesQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.get_securities.queryString,
    variables: GraphqlOperations.queries.get_securities.createVars(
      SecurityType.Transfer,
      SecurityListType.OwnHistory,
      selectedAccount,
    ),
    pause: selectedAccount === '',
    requestPolicy: 'network-only',
  })[0];

  // MARK: - Subscriptions

  const handleNewSecurityTransferSubscription = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    subscriptionSecurityTransfers: SecurityTransferListItem[] = [],
    response: Subscription,
  ): SecurityTransferListItem[] => {
    if (response && response.new_security_transfer) {
      resetList();
      reexecuteItemsResultQuery();
      reexecuteSumPortfolioLastUpdateQuery();
    }
    return [];
  };

  useSubscription(
    {
      query: GraphqlOperations.subscriptions.new_security_transfer.subscriptionString,
    },
    handleNewSecurityTransferSubscription,
  );

  const handleUpdateSecurityTransferSubscription = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    subscriptionSecurityTransfers: SecurityTransferListItem[] = [],
    response: Subscription,
  ): SecurityTransferListItem[] => {
    if (response && response.update_security_transfer) {
      const item = response.update_security_transfer;
      const itemsCopy = [...items];
      const itemToChangeIndex = itemsCopy.findIndex(
        (i) => i.transaction_code === item.transaction_code,
      );
      if (itemToChangeIndex >= 0) {
        itemsCopy[itemToChangeIndex] = item;
      }
      setItems(itemsCopy);
    }
    return [];
  };

  useSubscription(
    {
      query: GraphqlOperations.subscriptions.update_security_transfer.subscriptionString,
    },
    handleUpdateSecurityTransferSubscription,
  );

  // MARK: - Lifecycle methods

  useEffect(() => {
    if (
      itemsResultQuery.data?.get_security_transfers?.page_count !== undefined &&
      itemsResultQuery.data.get_security_transfers.page === currentPage
    ) {
      setItems([...items.concat(itemsResultQuery.data.get_security_transfers.security_transfers)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsResultQuery.data]);

  useEffect(() => {
    const accounts = userAccountsQueryResult.data?.getUser?.accounts;
    if (accounts && accounts[0]) {
      setSelectedAccount(accounts[0].number);
    }
  }, [userAccountsQueryResult]);

  // MARK: - Event handlers

  const handleCreateClick = (): void => {
    history.push(Paths.contact);
  };

  // MARK: - Helpers

  const resetList = (): void => {
    setCurrentPage(1);
    setItems([]);
  };

  const hasZeroItems =
    !itemsResultQuery.fetching &&
    itemsResultQuery.data?.get_security_transfers?.security_transfers &&
    items.length === 0;

  const noContent = (
    <p className="self-center text-12 py-6 text-gray" style={{ height: 'calc(100vh - 240px)' }}>
      <FormattedMessage id="securityTransfers.noContent" />
    </p>
  );

  const hasNextPage =
    itemsResultQuery.fetching ||
    (itemsResultQuery.data?.get_security_transfers?.page_count !== undefined &&
      itemsResultQuery.data?.get_security_transfers?.page !== undefined &&
      itemsResultQuery.data.get_security_transfers.page_count >
        itemsResultQuery.data.get_security_transfers.page);

  const userAccountOptions =
    userAccountsQueryResult.data?.getUser?.accounts?.map((account: Account) => ({
      value: account.number,
      label: `${account.type}: ${account.number}`,
    })) || [];

  return (
    <DefaultScreen
      background={assets.images.orders_bg}
      title={intl.formatMessage({ id: 'securityTransfers.title' })}
      updatedAt={formatDateTime(sumPortfolioLastUpdateQueryResult.data?.sum_portfolio?.last_update)}
      rightContent={
        userAccountsQueryResult.data?.getUser ? (
          <StyledReactSelect
            placeholder={intl.formatMessage({ id: 'common.accountSelectPlaceholder' })}
            type="blur"
            bold
            defaultValue={userAccountOptions[0]}
            options={userAccountOptions}
            onChange={(value) => {
              if (value !== selectedAccount) {
                resetList();
                setSelectedAccount(value as string);
                setSelectedInstruments([]);
              }
            }}
          />
        ) : undefined
      }
    >
      <BlurredContainerM2030 className="flex flex-col">
        <ListHeader
          createButtonLabel={intl.formatMessage({ id: 'securityTransfers.newInstructionButton' })}
          onCreateClick={handleCreateClick}
        >
          <ListControls
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            selectedOrderingType={selectedOrderingType}
            selectedOrderingDirection={selectedOrderingDirection}
            setSelectedOrderingDirection={setSelectedOrderingDirection}
            setSelectedOrderingType={setSelectedOrderingType}
            setSelectedInstruments={setSelectedInstruments}
            resetList={resetList}
            statusOptions={statusOptions}
            orderingOptions={securityTransfersOrderingOptions}
            selectedInstruments={selectedSecurities}
            istrumentPlaceholder={intl.formatMessage({ id: 'common.instrumentSelectPlaceholder' })}
            instrumentOptions={
              securitiesQueryResult.data?.get_securities.map((s) => ({
                value: s.short_name,
                label: s.name,
              })) || []
            }
          />
        </ListHeader>{' '}
        {hasZeroItems ? (
          noContent
        ) : (
          <InfiniteList
            hasNextPage={hasNextPage}
            isNextPageLoading={itemsResultQuery.fetching}
            items={items}
            loadNextPage={fetchNextPage}
            listItem={ListItem}
            desktopItemHeight={84}
            mobileItemHeight={170}
          />
        )}
      </BlurredContainerM2030>
    </DefaultScreen>
  );
};

export default withErrorBoundary(SecurityTransfers, { fallback: Fallback });
